import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LeadCard from "@/components/crm/LeadCard";
import { STATUTS } from "@/lib/crmHelpers";
import { insightsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";

const COLUMN_META = {
  nouveau: { dot: "bg-zinc-400" },
  contacte: { dot: "bg-blue-500" },
  interesse: { dot: "bg-amber-500" },
  rdv: { dot: "bg-violet-500" },
  gagne: { dot: "bg-emerald-500" },
  perdu: { dot: "bg-zinc-500" },
};

export default function KanbanBoard({
  leads,
  onOpenLead,
  onStatutChange,
  loading,
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [insightsMap, setInsightsMap] = useState({});
  const [scrollState, setScrollState] = useState({ canL: false, canR: false });
  const scrollerRef = useRef(null);
  const { activeProjectId } = useSettings();

  // Update arrow availability whenever the scroller moves or the layout changes.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      setScrollState({
        canL: el.scrollLeft > 4,
        canR: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [leads.length]);

  const scrollBy = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Fetch bulk insights once per project change.
  // Refresh when leads list changes (after CRUD).
  useEffect(() => {
    let cancelled = false;
    insightsApi
      .forProject(activeProjectId)
      .then((data) => {
        if (!cancelled) setInsightsMap(data || {});
      })
      .catch(() => {
        if (!cancelled) setInsightsMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, leads.length]);

  // v2.6.0 — `optimistic` mirrors `leads` but lets us pre-move a card to the
  // target column the instant the drop fires, so the UI feels snappy even on
  // slow connections. If the PATCH fails, we revert and toast.
  const [optimistic, setOptimistic] = useState(null); // {id, prevStatut, nextStatut} | null
  const effectiveLeads = optimistic
    ? leads.map((l) =>
        l.id === optimistic.id ? { ...l, statut: optimistic.nextStatut } : l
      )
    : leads;

  // Clear the optimistic patch once the parent `leads` list reflects the new
  // statut (server confirmed). Avoids stale local state if the user drags
  // again while a PATCH is in flight.
  useEffect(() => {
    if (!optimistic) return;
    const fresh = leads.find((l) => l.id === optimistic.id);
    if (fresh && fresh.statut === optimistic.nextStatut) {
      setOptimistic(null);
    }
  }, [leads, optimistic]);

  const byStatus = STATUTS.reduce((acc, s) => {
    acc[s.value] = effectiveLeads.filter((l) => l.statut === s.value);
    return acc;
  }, {});

  const handleDragStart = (id) => setDraggingId(id);
  const handleDragEnd = () => {
    setDraggingId(null);
    setOverColumn(null);
  };

  const handleDrop = async (statut) => {
    if (!draggingId) return;
    const lead = effectiveLeads.find((l) => l.id === draggingId);
    if (!lead) return;
    if (lead.statut === statut) {
      handleDragEnd();
      return;
    }
    // Optimistic move — UI updates BEFORE the PATCH round-trips.
    const prevStatut = lead.statut;
    setOptimistic({ id: lead.id, prevStatut, nextStatut: statut });
    handleDragEnd();
    try {
      await onStatutChange(lead.id, statut);
      // The parent will refetch and effectiveLeads will catch up via useEffect.
    } catch (e) {
      // Roll back the optimistic move and notify.
      setOptimistic(null);
      toast.error("Impossible de mettre à jour le statut");
    }
  };

  return (
    <div className="relative">
      {/* Floating left/right scroll arrows — make horizontal navigation
          obvious and recoverable on viewports where 5 columns don't fit. */}
      {scrollState.canL && (
        <button
          type="button"
          aria-label="Colonne précédente"
          onClick={() => scrollBy(-300)}
          className="absolute left-1 top-[44%] z-10 rounded-full border border-border/60 bg-background/95 p-1.5 shadow-md hover:bg-muted"
          data-testid="kanban-scroll-left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {scrollState.canR && (
        <button
          type="button"
          aria-label="Colonne suivante"
          onClick={() => scrollBy(300)}
          className="absolute right-1 top-[44%] z-10 rounded-full border border-border/60 bg-background/95 p-1.5 shadow-md hover:bg-muted"
          data-testid="kanban-scroll-right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    <div
      ref={scrollerRef}
      // `touch-pan-x` enables horizontal scroll on touch screens even though
      // cards capture HTML5 drag-and-drop. The container uses `overscroll-contain`
      // so vertical scrolling on the page isn't intercepted.
      className="scrollbar-thin flex h-[calc(100vh-260px)] min-h-[420px] gap-4 overflow-x-auto overflow-y-hidden pb-2 touch-pan-x overscroll-contain"
      data-testid="kanban-board"
    >
      {STATUTS.map((s) => {
        const items = byStatus[s.value] || [];
        const isOver = overColumn === s.value;
        const meta = COLUMN_META[s.value];
        return (
          <div
            key={s.value}
            data-testid={`kanban-column-${s.value}`}
            onDragOver={(e) => {
              e.preventDefault();
              // Tell the OS this is a move (cursor shows the move icon, not
              // the "forbidden" sign that some browsers default to).
              if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
              setOverColumn(s.value);
            }}
            onDragLeave={() => setOverColumn(null)}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(s.value);
            }}
            className={`kanban-column snap-start flex h-full w-[280px] flex-none flex-col rounded-xl border border-border/60 bg-muted/30 transition-colors ${
              isOver ? "drag-over" : ""
            } ${s.value === "perdu" ? "opacity-80" : ""}`}
          >
            <div className="flex flex-none items-center justify-between border-b border-border/40 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                <span className="font-heading text-sm font-semibold tracking-tight">
                  {s.label}
                </span>
              </div>
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-foreground/10 px-1.5 text-[11px] font-semibold text-foreground">
                {items.length}
              </span>
            </div>

            <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto px-2 py-2">
              {items.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  {loading ? "..." : "Vide"}
                </div>
              ) : (
                items.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    insights={insightsMap[lead.id]}
                    dragging={draggingId === lead.id}
                    onDragStart={() => handleDragStart(lead.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onOpenLead(lead)}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
    </div>
  );
}
